import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, Tabs, Tab, Divider, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import RepeatIcon from "@mui/icons-material/Repeat";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import TabletIcon from "@mui/icons-material/Tablet";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { isPayoneerDomain, stripHashtagsFromText } from "../../shared/businessPageComposer";
import "./ComposePage.css";

const LINKEDIN_LIMIT = 3000;

const DEVICES = [
  { label: "Mobile", icon: <SmartphoneIcon sx={{ fontSize: 16 }} />, maxWidth: 360 },
  { label: "Tablet", icon: <TabletIcon sx={{ fontSize: 16 }} />,     maxWidth: 540 },
  { label: "Web",    icon: <DesktopWindowsIcon sx={{ fontSize: 16 }} />, maxWidth: "100%" },
];

const renderStyledTextWithHashtags = (text) => {
  if (!text) return null;
  const hashtagRegex = /(#[a-zA-Z0-9_\u0900-\u097F]+)/g;
  const parts = text.split(hashtagRegex);

  return parts.map((part, index) => {
    if (part.match(hashtagRegex)) {
      return (
        <Typography
          key={index}
          component="span"
          sx={{
            color: "#0047AB", // Vibrant hashtag blue matching post cards
            fontWeight: 700,
            cursor: "pointer",
            display: "inline",
            fontFamily: "inherit",
            fontSize: "inherit",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {part}
        </Typography>
      );
    }
    return part;
  });
};

const PostPreview = ({ content = "", images = [], videos = [], documents = [], userProfile, businessPages = {} }) => {
  const isPayoneer = isPayoneerDomain(businessPages);
  const displayContent = isPayoneer ? stripHashtagsFromText(content) : content;
  const [deviceIndex, setDeviceIndex] = useState(0);
  const [mediaIndex, setMediaIndex] = useState(0);

  // Combine images, videos, and documents into one ordered media list for the carousel
  const allMedia = [
    ...images.map((f) => ({ file: f, type: "image" })),
    ...videos.map((f) => ({ file: f, type: "video" })),
    ...documents.map((f) => ({ file: f, type: "document" })),
  ];
  const mediaTotal = allMedia.length;

  // Reset carousel when media count changes
  useEffect(() => {
    setMediaIndex((i) => (i >= mediaTotal ? 0 : i));
  }, [mediaTotal]);

  const prevMedia = (e) => {
    e.stopPropagation();
    setMediaIndex((i) => (i - 1 + mediaTotal) % mediaTotal);
  };
  const nextMedia = (e) => {
    e.stopPropagation();
    setMediaIndex((i) => (i + 1) % mediaTotal);
  };

  const device    = DEVICES[deviceIndex];
  const charLeft  = LINKEDIN_LIMIT - content.length;
  const isEmpty   = content.trim() === "" && images.length === 0 && videos.length === 0 && documents.length === 0;

  const displayName = userProfile?.name || "Your Name";
  const avatarSrc   = userProfile?.profilePicture || userProfile?.profilePictureUrl || null;
  const initials    = displayName.charAt(0).toUpperCase();

  const charCounterStyle = {
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    color: charLeft < 0 ? "#DA4040" : charLeft < 300 ? "#F7C333" : "#2e7d32",
  };

  return (
    <Box className="preview-panel">

      {/* Header */}
      <Box className="preview-header">
        <Typography sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16, color: "#3B3B3B" }}>
          Live Preview
        </Typography>
        <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#95919D", mt: 0.3, mb: 1.5 }}>
          Updates in real time as you type
        </Typography>
      </Box>

      {/* Device tabs */}
      <Box className="preview-device-tabs">
        <Tabs
          value={deviceIndex}
          onChange={(_, v) => setDeviceIndex(v)}
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              minWidth: 80,
              fontFamily: "Poppins",
              fontSize: 12,
              fontWeight: 500,
              textTransform: "none",
              color: "#95919D",
              py: 0.75,
              px: 1.5,
              mr: "6px",
              borderRadius: "8px 8px 0 0",
              transition: "color 0.15s, background 0.15s",
            },
            "& .MuiTab-root:hover": { color: "#0047AB", background: "#F0F4FF" },
            "& .Mui-selected": { color: "#0047AB !important", fontWeight: 600 },
            "& .MuiTabs-indicator": { backgroundColor: "#0047AB", height: "2px" },
          }}
        >
          {DEVICES.map((d) => (
            <Tab
              key={d.label}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  {d.icon}
                  <span>{d.label}</span>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      <Divider sx={{ borderColor: "#DFDFDF" }} />

      {/* Preview frame */}
      <Box className="preview-frame-area">
        <Box sx={{
          width: "100%",
          maxWidth: device.maxWidth,
          transition: "max-width 0.3s ease",
          mx: "auto",
          ...(deviceIndex < 2 && {
            boxShadow: "0 0 0 2px #DFDFDF, 0 8px 24px rgba(0,0,0,0.12)",
            borderRadius: "12px",
          }),
        }}>
          {isEmpty ? (
            <Box className="preview-empty">
              <Typography sx={{ fontFamily: "Poppins", fontSize: 13, color: "#95919D" }}>
                Start writing your post to see a live preview here.
              </Typography>
            </Box>
          ) : (
            <Box className="preview-card">

              {/* Post author */}
              <Box sx={{ display: "flex", alignItems: "flex-start", p: 2, pb: 1, gap: 1.5 }}>
                <Avatar
                  src={avatarSrc}
                  sx={{ width: 44, height: 44, bgcolor: "#0047AB", fontSize: 18, fontWeight: 700 }}
                >
                  {!avatarSrc && initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    <Typography sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14, color: "#191919" }}>
                      {displayName}
                    </Typography>
                    <Box
                      sx={{
                        width: 15,
                        height: 15,
                        borderRadius: "50%",
                        bgcolor: "#666666",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: 9,
                        fontWeight: "bold",
                      }}
                    >
                      ✓
                    </Box>
                    <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#666666", fontWeight: 500 }}>
                      • You
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: 11,
                      color: "#666666",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      mt: 0.2,
                    }}
                  >
                    {userProfile?.headline || userProfile?.designation || userProfile?.bio }
                  </Typography>
                  <Typography sx={{ fontFamily: "Poppins", fontSize: 11, color: "#666666", mt: 0.2, display: "flex", alignItems: "center", gap: 0.5 }}>
                    now · 🌐
                  </Typography>
                </Box>
                <IconButton size="small" sx={{ color: "#666" }}>
                  <MoreHorizIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>

              {/* Post content */}
              <Box sx={{ px: 2, py: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: deviceIndex === 0 ? 13 : 14,
                    lineHeight: 1.65,
                    color: "#191919",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {renderStyledTextWithHashtags(displayContent)}
                </Typography>
              </Box>

              {/* Media carousel / Marquee frame */}
              {mediaTotal > 0 && (
                <Box className="preview-card-media" sx={{ position: "relative", bgcolor: "#000", overflow: "hidden" }}>
                  {/* Current media item */}
                  {allMedia[mediaIndex]?.type === "image" ? (
                    <img
                      key={mediaIndex}
                      src={allMedia[mediaIndex].file instanceof File ? URL.createObjectURL(allMedia[mediaIndex].file) : (allMedia[mediaIndex].file?.url || allMedia[mediaIndex].file)}
                      alt={`preview ${mediaIndex + 1}`}
                      style={{ width: "100%", maxHeight: 380, minHeight: 250, objectFit: "cover", display: "block" }}
                    />
                  ) : allMedia[mediaIndex]?.type === "video" ? (
                    <video
                      key={mediaIndex}
                      src={allMedia[mediaIndex].file instanceof File ? URL.createObjectURL(allMedia[mediaIndex].file) : (allMedia[mediaIndex].file?.url || allMedia[mediaIndex].file)}
                      controls
                      style={{ width: "100%", maxHeight: 380, minHeight: 250, objectFit: "cover", display: "block", background: "#000" }}
                    />
                  ) : (
                    (() => {
                      const doc = allMedia[mediaIndex]?.file;
                      const getDocUrl = (d) => {
                        if (typeof d === "string") return d;
                        if (d?.url) return d.url;
                        if (d?.fileUrl) return d.fileUrl;
                        if (d instanceof File || d instanceof Blob) {
                          try { return URL.createObjectURL(d); } catch (e) { return null; }
                        }
                        if (d?.file instanceof File || d?.file instanceof Blob) {
                          try { return URL.createObjectURL(d.file); } catch (e) { return null; }
                        }
                        return null;
                      };

                      const docName = doc?.name || doc?.fileName || (typeof doc === "string" ? doc.split("/").pop() : "Document");
                      const docUrl = getDocUrl(doc);
                      const isPdf = docName.toLowerCase().endsWith(".pdf") || doc?.type === "application/pdf" || (docUrl && docUrl.toLowerCase().includes(".pdf"));

                      let viewerUrl = docUrl;
                      if (docUrl) {
                        if (docUrl.startsWith("blob:") || isPdf) {
                          viewerUrl = `${docUrl}#toolbar=0&navpanes=0&scrollbar=0`;
                        } else {
                          const encodedUrl = encodeURIComponent(docUrl);
                          viewerUrl = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
                        }
                      }

                      return (
                        <Box
                          key={mediaIndex}
                          sx={{
                            position: "relative",
                            height: 320,
                            width: "100%",
                            overflow: "hidden",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          {docUrl ? (
                            <>
                              <iframe
                                src={viewerUrl}
                                title={docName}
                                style={{
                                  width: "calc(100% + 15px)",
                                  height: "calc(100% + 55px)",
                                  border: 0,
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                }}
                              />
                              <IconButton
                                component="a"
                                href={docUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  position: "absolute",
                                  top: 10,
                                  right: 48,
                                  backgroundColor: "rgba(255,255,255,0.9)",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                  "&:hover": { backgroundColor: "#ffffff" },
                                  zIndex: 3,
                                }}
                                size="small"
                              >
                                <OpenInNewIcon sx={{ color: "#1D4ED8", fontSize: 16 }} />
                              </IconButton>
                            </>
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 2 }}>
                              <Typography sx={{ fontFamily: "Poppins", fontSize: 13, color: "#1D4ED8", fontWeight: 500 }}>
                                📄 {docName}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()
                  )}

                  {/* Arrow navigation */}
                  {mediaTotal > 1 && (
                    <>
                      <IconButton
                        onClick={prevMedia}
                        size="small"
                        sx={{
                          position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                          bgcolor: "rgba(0,0,0,0.55)", color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                          width: 32, height: 32, zIndex: 4,
                        }}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <IconButton
                        onClick={nextMedia}
                        size="small"
                        sx={{
                          position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                          bgcolor: "rgba(0,0,0,0.55)", color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                          width: 32, height: 32, zIndex: 4,
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 20 }} />
                      </IconButton>

                      {/* Dot indicators */}
                      <Box sx={{
                        position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)",
                        display: "flex", gap: "6px", zIndex: 4,
                      }}>
                        {allMedia.map((_, i) => (
                          <Box
                            key={i}
                            onClick={(e) => { e.stopPropagation(); setMediaIndex(i); }}
                            sx={{
                              width: i === mediaIndex ? 18 : 7,
                              height: 7,
                              borderRadius: 4,
                              bgcolor: i === mediaIndex ? "#fff" : "rgba(255,255,255,0.5)",
                              cursor: "pointer",
                              transition: "width 0.2s, background 0.2s",
                            }}
                          />
                        ))}
                      </Box>

                      {/* Counter badge */}
                      <Box sx={{
                        position: "absolute", top: 10, right: 12,
                        bgcolor: "rgba(0,0,0,0.65)", color: "#fff",
                        borderRadius: "6px", px: "8px", py: "3px",
                        fontFamily: "Poppins", fontSize: 11, fontWeight: 600,
                        lineHeight: "16px", whiteSpace: "nowrap",
                        zIndex: 4,
                      }}>
                        {mediaIndex + 1} / {mediaTotal}
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* Action Bar (LinkedIn & Twitter style) */}
              <Divider sx={{ mx: 2, borderColor: "#EDEFF2", mt: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", px: 2, py: 1 }}>
                {[
                  { icon: <ThumbUpAltOutlinedIcon sx={{ fontSize: 18 }} />, label: "Like" },
                  { icon: <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />,  label: "Comment" },
                  { icon: <RepeatIcon sx={{ fontSize: 18 }} />,             label: "Repost" },
                  { icon: <SendOutlinedIcon sx={{ fontSize: 18 }} />,       label: "Send" },
                ].map(({ icon, label }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      py: 0.8,
                      px: 1.2,
                      borderRadius: "6px",
                      color: "#666666",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "#F3F3F3",
                        color: "#0047AB",
                      },
                    }}
                  >
                    {icon}
                    <Typography sx={{ fontFamily: "Poppins", fontSize: 12, fontWeight: 600 }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>

            </Box>
          )}
        </Box>
      </Box>

      {/* Character counter footer */}
      <Box className="preview-char-footer">
        <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#95919D" }}>
          LinkedIn limit
        </Typography>
        <Typography sx={charCounterStyle}>
          {charLeft < 0
            ? `${Math.abs(charLeft)} over limit`
            : `${charLeft.toLocaleString()} remaining`}
        </Typography>
      </Box>

    </Box>
  );
};

export default PostPreview;

